import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  MessageCircle, 
  Palette, 
  Mountain, 
  Store, 
  Users,
  Activity
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { fetchAllEvents } from '../services/eventsService';
import { getPromptsFromFirestore } from '../services/promptsService';
import { getDashboardStats } from '../services/dashboardService';
import { db } from '../firebase';
import { EventItem } from '../types';

interface PromptLike {
  id: string;
  geniusLociId?: string;
  personality?: string;
}

interface GeniusLociLike {
  name?: string;
}

const Home = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [prompts, setPrompts] = useState<Record<string, any>>({});
  const [geniusLociMap, setGeniusLociMap] = useState<Record<string, GeniusLociLike>>({});
  const [stats, setStats] = useState({
    experiences: 0,
    geniusLoci: 0,
    events: 0,
    artworks: 0,
    trails: 0,
    producers: 0,
    localActivities: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [eventsData, promptsData, dashboardStats, geniusLociSnapshot] = await Promise.all([
          fetchAllEvents(),
          getPromptsFromFirestore(),
          getDashboardStats(),
          getDocs(collection(db, 'geniusLoci'))
        ]);

        const geniusLociData: Record<string, GeniusLociLike> = {};

        setEvents(eventsData);
        setPrompts(promptsData);
        setStats(dashboardStats);
        geniusLociSnapshot.docs.forEach((snapshotDoc: any) => {
          geniusLociData[snapshotDoc.id] = snapshotDoc.data() as GeniusLociLike;
        });
        setGeniusLociMap(geniusLociData);
        console.log('📊 [DASHBOARD] Dati caricati:', dashboardStats);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const statsCards = [
    {
      name: 'Esperienze',
      value: stats.experiences.toString(),
      icon: Activity,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      href: '/esperienze'
    },
    {
      name: 'Genius Loci',
      value: stats.geniusLoci.toString(),
      icon: Users,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      href: '/prompt'
    },
    {
      name: 'Eventi',
      value: stats.events.toString(),
      icon: Calendar,
      color: 'text-tertiary',
      bgColor: 'bg-tertiary/10',
      href: '/eventi'
    },
    {
      name: 'Opere d\'Arte',
      value: stats.artworks.toString(),
      icon: Palette,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      href: '/opere'
    },
    {
      name: 'Sentieri',
      value: stats.trails.toString(),
      icon: Mountain,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      href: '/sentieri'
    },
    {
      name: 'Attività Locali',
      value: (stats.producers + stats.localActivities).toString(),
      icon: Store,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      href: '/attivita-locali'
    }
  ];

  const recentEvents = events.slice(0, 5);

  const getGeniusLociName = (prompt: PromptLike) => {
    if (!prompt.geniusLociId) return 'Genius Loci da configurare';
    const mappedName = geniusLociMap[prompt.geniusLociId]?.name?.trim();
    if (mappedName) return mappedName;
    return 'Genius Loci da configurare';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Dashboard</h1>
        <p className="text-text-secondary">
          Gestisci tutti i contenuti dell'app T-Alento
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="card-hover p-6 group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary group-hover:text-text-primary transition-colors duration-200">
                    {stat.name}
                  </p>
                  <p className="text-2xl font-bold text-text-primary mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">
              Eventi Recenti
            </h2>
            <Link 
              to="/eventi" 
              className="text-primary hover:text-secondary text-sm font-medium"
            >
              Vedi tutti
            </Link>
          </div>
          <div className="space-y-3">
            {recentEvents.length > 0 ? (
              recentEvents.map((event) => (
                <div key={event.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {event.title}
                    </p>
                    <p className="text-xs text-text-secondary">
                      {event.startAt.toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-text-secondary text-sm">Nessun evento recente</p>
            )}
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">
              Genius Loci Attivi
            </h2>
            <Link 
              to="/prompt" 
              className="text-primary hover:text-secondary text-sm font-medium"
            >
              Gestisci
            </Link>
          </div>
          <div className="space-y-3">
            {Object.values(prompts).slice(0, 5).map((prompt: any) => (
              <div key={prompt.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-secondary/20 rounded-full flex items-center justify-center">
                  <MessageCircle size={16} className="text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {getGeniusLociName(prompt)}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {prompt.personality || 'Genius Loci'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">
          Azioni Rapide
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/eventi"
            className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-primary/5 transition-colors duration-200"
          >
            <Calendar className="text-primary" size={20} />
            <span className="text-sm font-medium text-text-primary">Nuovo Evento</span>
          </Link>
          <Link
            to="/prompt"
            className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-secondary/5 transition-colors duration-200"
          >
            <MessageCircle className="text-secondary" size={20} />
            <span className="text-sm font-medium text-text-primary">Nuovo Prompt</span>
          </Link>
          <Link
            to="/opere"
            className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-tertiary/5 transition-colors duration-200"
          >
            <Palette className="text-tertiary" size={20} />
            <span className="text-sm font-medium text-text-primary">Nuova Opera</span>
          </Link>
          <Link
            to="/sentieri"
            className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-accent/5 transition-colors duration-200"
          >
            <Mountain className="text-accent" size={20} />
            <span className="text-sm font-medium text-text-primary">Nuovo Sentiero</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;
